import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/lib/api";
import { Link } from "react-router-dom";
import { company, phones } from "@/data/company";
import { useLangPath } from "@/components/useLangPath";

export default function Footer() {
  const { t } = useTranslation();
  const withLang = useLangPath();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter`, { email });
      setStatus("ok");
      setMsg(t("footer.newsletter_thanks"));
      setEmail("");
    } catch (err) {
      setStatus("err");
      setMsg(err?.response?.data?.detail?.[0]?.msg || t("waitlist.error_default"));
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-brand-dark text-white pt-16 pb-8 border-t border-zinc-800">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="inline-block mb-4">
              <img
                src="/logos/aocems.svg"
                alt="AOCEMS"
                data-testid="footer-logo"
                className="h-14 w-auto object-contain"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed mb-3">
              {t("footer.company_blurb")}
            </p>
            <p className="text-zinc-500 text-xs leading-relaxed mb-3">
              {t("footer.parent_blurb")}
            </p>
            <p className="text-zinc-500 text-xs leading-relaxed mb-2">
              <span className="text-brand font-bold">{t("footer.hq_label")}</span> {t("footer.hq_address")}
            </p>
            <p className="text-zinc-500 text-xs leading-relaxed mb-6">
              {t("footer.offices_in")}
            </p>
            <div className="flex gap-4">
              <a
                href={`https://${company.domain}`}
                title={company.domain}
                className="material-symbols-outlined cursor-pointer hover:text-brand transition-colors"
                data-testid="footer-website-link"
              >
                language
              </a>
              <a
                href={`mailto:${company.email.contact}`}
                title={company.email.contact}
                className="material-symbols-outlined cursor-pointer hover:text-brand transition-colors"
                data-testid="footer-email-link"
              >
                mail
              </a>
              <a
                href={`tel:${phones[0].tel}`}
                title={phones[0].number}
                className="material-symbols-outlined cursor-pointer hover:text-brand transition-colors"
                data-testid="footer-phone-link"
              >
                call
              </a>
            </div>
          </div>

          <div className="md:col-span-2 grid grid-cols-2 gap-8">
            <div>
              <h5 className="font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-6">{t("footer.links.platform")}</h5>
              <ul className="space-y-4 text-sm text-zinc-400">
                <li><Link to={withLang("/solutions")} className="hover:text-brand transition-colors">{t("nav.solutions")}</Link></li>
                <li><Link to={withLang("/how-it-works")} className="hover:text-brand transition-colors">{t("nav.how_it_works")}</Link></li>
                <li><Link to={withLang("/technology")} className="hover:text-brand transition-colors">{t("nav.technology")}</Link></li>
                <li><Link to={withLang("/case-studies")} className="hover:text-brand transition-colors">{t("nav.case_studies")}</Link></li>
                <li><Link to={withLang("/media")} className="hover:text-brand transition-colors">{t("nav.media")}</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-6">{t("footer.links.compliance")}</h5>
              <ul className="space-y-4 text-sm text-zinc-400">
                <li><Link to={withLang("/governance")} className="hover:text-brand transition-colors">{t("nav.governance")}</Link></li>
                <li><Link to={withLang("/resources")} className="hover:text-brand transition-colors">{t("footer.links.resources")}</Link></li>
                <li><Link to={withLang("/contact")} className="hover:text-brand transition-colors">{t("footer.links.contact_us")}</Link></li>
                <li><Link to={withLang("/privacy")} className="hover:text-brand transition-colors" data-testid="footer-privacy-link">{t("footer.links.privacy_policy")}</Link></li>
              </ul>
            </div>
          </div>

          <div>
            <h5 className="font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-6">{t("footer.stay_informed")}</h5>
            <form onSubmit={submit} data-testid="newsletter-form" className="flex">
              <input
                data-testid="newsletter-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-l-lg text-sm w-full focus:ring-brand focus:border-brand text-white px-3 py-2 outline-none"
                placeholder={t("footer.newsletter_placeholder")}
                type="email"
                required
              />
              <button
                data-testid="newsletter-submit"
                disabled={status === "loading"}
                className="bg-brand px-4 rounded-r-lg disabled:opacity-60"
                type="submit"
              >
                <span className="material-symbols-outlined text-white">chevron_right</span>
              </button>
            </form>
            {status !== "idle" && (
              <p
                data-testid="newsletter-message"
                className={`mt-3 text-xs ${status === "ok" ? "text-brand" : status === "err" ? "text-red-400" : "text-zinc-400"}`}
              >
                {status === "loading" ? t("waitlist.submitting") : msg}
              </p>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <span className="px-2.5 py-1 rounded-full bg-zinc-900 text-[10px] text-zinc-400 border border-zinc-800 uppercase tracking-widest">ISO 14065</span>
              <span className="px-2.5 py-1 rounded-full bg-zinc-900 text-[10px] text-zinc-400 border border-zinc-800 uppercase tracking-widest">IMO GreenVoyage 2050</span>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-zinc-800 text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-manrope text-[10px] uppercase tracking-widest text-zinc-500">
            © {new Date().getFullYear()} {company.parent}™. {t("footer.rights")} · {company.domain} · AOCEMS · {t("footer.trademark_label")}
          </p>
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-brand text-sm">security</span>
            <span className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">Enterprise Secure · SOC2 Type II</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
